import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { API_URL, fmtUGX, downloadFeesExport } from './feesUtils';

export const ArrearsTab = ({ school, year, term, refresh, refreshKey }) => {
  const [data, setData] = useState(null);
  const [cfOpen, setCfOpen] = useState(false);
  const [cf, setCf] = useState({ to_academic_year: year, to_term: term < 3 ? term + 1 : 1 });

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/fees/arrears`, { params: { school } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [school]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const doCarryForward = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/fees/carry-forward`, {
        school, academic_year: year, term,
        to_academic_year: cf.to_academic_year, to_term: cf.to_term,
      });
      toast.success(r.data.message);
      setCfOpen(false);
      load();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Carry-forward failed');
    }
  };

  const S = data?.summary || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="arrears-summary">
        {[['Students with Arrears', S.students_with_arrears], ['Total Brought Forward', fmtUGX(S.total_brought_forward)],
          ['Arrears Collected', fmtUGX(S.arrears_collected)], ['Remaining Arrears', fmtUGX(S.remaining_arrears)]].map(([l, v]) => (
            <Card key={l}><CardContent className="p-3">
              <p className="text-xs text-muted-foreground">{l}</p>
              <p className="text-base font-heading font-bold">{v ?? 0}</p>
            </CardContent></Card>
          ))}
      </div>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="font-heading text-base">Arrears Tracker</CardTitle>
            <CardDescription>Unpaid balances carried forward across terms — never erased until paid or adjusted</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadFeesExport('arrears',
              { school, academic_year: year, term, format: 'excel' }, `arrears_${year}_T${term}.xlsx`)} data-testid="arrears-export">Export Excel</Button>
            <Button size="sm" onClick={() => setCfOpen(true)} data-testid="carry-forward-btn">
              <ArrowRightLeft className="h-4 w-4 mr-1" /> Carry Forward Term Balances
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Student</TableHead><TableHead>Class</TableHead><TableHead>From Term</TableHead>
              <TableHead className="text-right">Brought Forward</TableHead>
              <TableHead className="text-right">Paid Toward Arrears</TableHead>
              <TableHead className="text-right">Remaining</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {!data || data.items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No arrears recorded for this school</TableCell></TableRow>
              ) : data.items.map((a) => (
                <TableRow key={a.id} data-testid={`arrears-row-${a.id}`}>
                  <TableCell className="font-medium">{a.student_name}</TableCell>
                  <TableCell>{a.class_name}</TableCell>
                  <TableCell className="text-xs">{a.source_academic_year} Term {a.source_term} → {a.to_academic_year} Term {a.to_term}</TableCell>
                  <TableCell className="text-right">{fmtUGX(a.amount_brought_forward)}</TableCell>
                  <TableCell className="text-right text-green-600">{fmtUGX(a.amount_paid)}</TableCell>
                  <TableCell className={`text-right font-semibold ${a.remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmtUGX(a.remaining)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={cfOpen} onOpenChange={setCfOpen}>
        <DialogContent className="max-w-md" data-testid="carry-forward-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Carry Forward Unpaid Balances</DialogTitle>
            <DialogDescription>
              Every student's unpaid balance from {year} Term {term} will be recorded as arrears in the selected term. Historical records stay intact.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>To Academic Year</Label>
              <Select value={String(cf.to_academic_year)} onValueChange={(v) => setCf({ ...cf, to_academic_year: parseInt(v) })}>
                <SelectTrigger data-testid="cf-year"><SelectValue /></SelectTrigger>
                <SelectContent>{[year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>To Term</Label>
              <Select value={String(cf.to_term)} onValueChange={(v) => setCf({ ...cf, to_term: parseInt(v) })}>
                <SelectTrigger data-testid="cf-term"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3].map((t) => <SelectItem key={t} value={String(t)}>Term {t}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCfOpen(false)}>Cancel</Button>
            <Button onClick={doCarryForward} data-testid="cf-confirm">Carry Forward</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
